import { List } from 'immutable';
import { find, get, isEmpty, isEqual, property, reduce } from 'lodash';
import React from 'react';
import { formatNumber } from '../../../../../utils/format';
import { isDividendsIncome } from '../private/forms/IncomesForm/IncomesFromRFComponent/IncomeService';
import NDFL3YearLogic from './NDFL3YearsLogic/NDFL3YearLogic';

/**
 * @type {Function}
 */
export const selectValuePicker = property('value');

/**
 * @param {number} taxDeductionCodeValue
 * @param {string} incomeSum
 * @param {string} numChildren
 * @param {number} incomeCode
 * @param {Array<{deductionCode: number, incomeCode: number, limit: number|string, maxPercent: number}>} deductionTypes
 * @param {number} shareNumerator
 * @param {number} shareDenominator
 * @returns {number|string|null}
 */
export const onChangeTaxDeductionCode = function (
  taxDeductionCodeValue,
  incomeSum,
  numChildren,
  incomeCode,
  deductionTypes,
  shareNumerator,
  shareDenominator,
) {
  if (taxDeductionCodeValue === 0) {
    return 0;
  }

  const deductionType = find(
    deductionTypes,
    (item) => item.deductionCode === taxDeductionCodeValue && item.incomeCode === incomeCode,
  );
  const limit = get(deductionType, 'limit');
  const maxPercent = get(deductionType, 'maxPercent');
  const incomeSumNum = parseFloat(incomeSum);

  if (maxPercent && incomeSumNum) {
    // Если в поле «Вид дохода» выбран доход одного из видов 2201-2209 и в поле
    // «Предоставить налоговый вычет» выбран вычет вида «405 – в пределах норматива расходов».
    // Заполняется значением, рассчитанным по формуле:
    // <значение поля «Сумма дохода»> * <значение атрибута «Размер вычета по нормативу»> / 100
    if (
      taxDeductionCodeValue === 405 &&
      [2201, 2202, 2203, 2204, 2205, 2206, 2207, 2208, 2209].includes(incomeCode)
    ) {
      return Number((incomeSumNum * maxPercent) / 100).toFixed(2);
    }
  }

  if (limit) {
    let resultDeductionSum = 0;

    if (taxDeductionCodeValue === 904 && [1511].includes(incomeCode)) {
      // Если поле «Размер доли в праве» или «Сумма дохода» не заполнено, значение по умолчанию не заполняется
      if (incomeSumNum && shareNumerator && shareDenominator) {
        resultDeductionSum = Number(limit * (shareNumerator / shareDenominator)).toFixed(2);

        if (resultDeductionSum <= incomeSumNum) {
          // Если поле «Размер доли в праве» заполнено и значение атрибута «максимальный размер вычета» для данного вычета,
          // умноженное на значение поля «Размер доли в праве», меньше или равно значению поля «Сумма дохода»,
          // то рассчитывается по формуле <значение атрибута «максимальный размер вычета»>*<значение поля «Размер доли в праве»>
          return resultDeductionSum;
        } else {
          // Если поле «Размер доли в праве» заполнено и значение атрибута «максимальный размер вычета» для данного вычета,
          // умноженное на значение поля «Размер доли в праве», больше значения поля «Сумма дохода»,
          // то поле заполняется значением поля «Сумма дохода»
          return incomeSumNum;
        }
      } else {
        return null;
      }
    }
    // 1.
    // Для вычета вида «508-Вычет из суммы единоврем. мат. помощи при рождении (усынов.) ребенка»:
    //  Если поле «Количество детей» заполнено, то рассчитывается по формуле
    //  <значение атрибута «максимальный размер вычета»>*<значение поля «Количество детей»>
    //  Если поле «Количество детей» не заполнено, значение рассчитывается аналогично другим видам вычетов.
    else if (taxDeductionCodeValue === 508 && parseInt(numChildren) > 0) {
      resultDeductionSum = limit * parseInt(numChildren);
    }
    // 2.
    // Для других видов вычетов:
    //  значение атрибута «Максимальный размер вычета» из справочника «Налоговые вычеты и суммы,
    //  уменьшающие налоговую базу» для вычета, выбранного в поле «Предоставить налоговый вычет»,
    //  если указанное значение непустое и не превышает сумму, указанную пользователем в поле «Сумма дохода».
    else if (limit <= incomeSum) {
      resultDeductionSum = limit;
    }
    // 3.
    // Если атрибут «Максимальный размер вычета» пуст или не введена сумма дохода – пусто.
    else if (!limit || !incomeSum) {
      resultDeductionSum = 0;
    }
    // 4.
    // Если значение атрибута «Максимальная сумма вычета» превышает значение поля «Сумма дохода»,
    // то поле заполняется значением поля «Сумма дохода» соответствующего дохода.
    else if (limit || limit > incomeSum) {
      resultDeductionSum = incomeSum;
    }

    if (resultDeductionSum && resultDeductionSum > incomeSumNum) {
      resultDeductionSum = incomeSumNum;
    }

    return resultDeductionSum ? resultDeductionSum : null;
  } else {
    return null;
  }
};

/**
 * @param {number} incomeCode
 * @param {number} [taxDeductionCode]
 * @returns {boolean}
 */
export function disabledDeductionSum(incomeCode, taxDeductionCode) {
  return (
    [2740, 2760, 2761, 2720, 2730, 2770, 2790, 1211].includes(incomeCode) ||
    ([2201, 2202, 2203, 2204, 2205, 2206, 2207, 2208, 2209].includes(incomeCode) &&
      taxDeductionCode === 405)
  );
}

/**
 * @param {number} incomeCode
 * @param {number} taxDeductionCode
 * @returns {boolean}
 */
export function disabledDeductionCodePicker(incomeCode, taxDeductionCode) {
  return incomeCode === 1530 && [208, 216, 222].includes(taxDeductionCode);
}

/**
 * @param {array} taxDeductionOptions
 * @param {number} incomeCode
 * @param {number} taxDeductionCode
 * @returns {boolean}
 */
export function showDeductionSum(taxDeductionOptions, incomeCode, taxDeductionCode) {
  return (
    !isEmpty(taxDeductionOptions) &&
    taxDeductionCode > 0 &&
    !(incomeCode === 1530 && taxDeductionCode == 618)
  );
}

/**
 * @param {{}} props
 * @param {{}} newProps
 * @param {string[]} propsArray
 * @returns {boolean}
 */
export function propsHasChanged(props, newProps, propsArray) {
  return reduce(
    propsArray,
    (res, prop) => {
      return res || !isEqual(newProps[prop], props[prop]);
    },
    false,
  );
}

/**
 * @param {string} sheetName
 * @param {number} incomeSourceIndex
 * @param {number?} incomeIndex
 * @returns {{}}
 */
export const sheetFieldsMapping = (sheetName, incomeSourceIndex, incomeIndex) => {
  const sheetMap = {
    sheetA: {
      incomes: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes`,
      taxDeductionCode: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].taxDeductionCode`,
      numChildren: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].numChildren`,
      incomeAmountRub: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].incomeSum`,
      incomeTypeCode: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].incomeTypeCodeASVK`,
      deductionSum: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].deductionSum`,
      shareNumerator: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].shareNumerator`,
      shareDenominator: `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}].incomes[${incomeIndex}].shareDenominator`,
    },
    sheetB: {
      incomes: `Ndfl3Package.payload.sheetB.sources`,
      taxDeductionCode: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].taxDeductionCode`,
      numChildren: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].numChildren`,
      incomeAmountRub: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].incomeAmountRub`,
      incomeTypeCode: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].incomeTypeCode`,
      deductionSum: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].deductionSum`,
      shareNumerator: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].shareNumerator`,
      shareDenominator: `Ndfl3Package.payload.sheetB.sources[${incomeSourceIndex}].shareDenominator`,
    },
  };
  return sheetMap[sheetName];
};

export function validateDeductionSum(deductionTypes, fieldMap) {
  return (value, values) => {
    value = Number(value);
    const curTaxDeductionSum = value;
    const curTaxDeductionCode = values.getIn(
      fieldMap.taxDeductionCode.replace(/]/g, '').split(/[\.\[]/),
    );
    const curNumChildren = values.getIn(fieldMap.numChildren.replace(/]/g, '').split(/[\.\[]/));
    const curIncomeTypeCode = parseFloat(
      values.getIn(fieldMap.incomeTypeCode.replace(/]/g, '').split(/[\.\[]/)),
    );
    const curIncomeSum = parseFloat(
      values.getIn(fieldMap.incomeAmountRub.replace(/]/g, '').split(/[\.\[]/)),
    );

    const sources = values.getIn(['Ndfl3Package', 'payload', 'sheetA', 'sources']);
    let totalIncomeSum = 0;
    let totalDeductionSum = 0;

    sources.forEach((source) => {
      source.get('incomes', List()).forEach((income) => {
        // sheetFieldsMapping('sheetB', this.props.index), this.props.index)
        const deductionCode = income.get('taxDeductionCode');
        const deductionSum = parseFloat(income.get('deductionSum'));
        const incomeSum = parseFloat(income.get('incomeSum'));
        const incomeCode = income.get('incomeTypeCodeASVK');
        if (
          (curIncomeTypeCode === incomeCode && deductionCode === curTaxDeductionCode) ||
          ([1510, 1511].includes(curIncomeTypeCode) &&
            [901, 904].includes(curTaxDeductionCode) &&
            [1510, 1511].includes(incomeCode) &&
            [901, 904].includes(deductionCode))
        ) {
          totalIncomeSum += incomeSum;
          totalDeductionSum += deductionSum;
        }
      });
    });

    const incomesSheetB = values.getIn(['Ndfl3Package', 'payload', 'sheetB', 'sources']);
    incomesSheetB.forEach((income) => {
      const deductionCode = income.get('taxDeductionCode');
      const deductionSum = parseFloat(income.get('deductionSum')) || 0;
      const incomeSum = parseFloat(income.get('incomeAmountRub')) || 0;
      const incomeCode = income.get('incomeTypeCode');
      if (
        (curIncomeTypeCode === incomeCode && deductionCode === curTaxDeductionCode) ||
        ([1510, 1511].includes(curIncomeTypeCode) &&
          [901, 904].includes(curTaxDeductionCode) &&
          [1510, 1511].includes(incomeCode) &&
          [901, 904].includes(deductionCode))
      ) {
        totalIncomeSum += incomeSum;
        totalDeductionSum += deductionSum;
      }
    });

    let limit = get(
      find(deductionTypes, (item) => item.deductionCode === curTaxDeductionCode),
      'limit',
    );

    if (isDividendsIncome(curIncomeTypeCode)) {
      return null;
    }

    if ([1510, 1511].includes(curIncomeTypeCode) && [901, 904].includes(curTaxDeductionCode)) {
      if (totalDeductionSum > 1000000) {
        const errorValue = 1000000 - (totalDeductionSum - curTaxDeductionSum);

        let error = null;

        if (errorValue > 0) {
          error = `Размер вычета не должен превышать ${formatNumber(errorValue)} руб.`;
        }

        if (errorValue <= 0) {
          error = `Вычет не может быть применен. Предельный размер вычета составляет ${limit} руб. в целом по всем доходам данного вида`;
        }

        return error;
      }
    }

    if (curTaxDeductionCode === 508 && limit && curNumChildren) {
      // 508 - Вычет из суммы единоврем. мат. помощи при рождении (усынов.) ребенка»
      totalDeductionSum = Number(value);
      totalIncomeSum = curIncomeSum;
      limit = limit * curNumChildren;
    }

    if (limit && Number(totalDeductionSum) > limit) {
      return `Превышен максимальный размер вычета ${limit} руб.`;
    }

    if (
      totalDeductionSum > totalIncomeSum &&
      !NDFL3YearLogic.logic.incomeCodesCanBeMoreThanIncomeSum(curIncomeTypeCode)
    ) {
      return 'Сумма вычета не должна превышать сумму дохода';
    }

    return null;
  };
}

/**
 * @param {string} title
 * @param {number} index
 * @param {Function} remove
 * @returns {Node}
 */
export function renderSourcesTitle(title, index, remove) {
  const handleLinkClick = (event) => {
    event.stopPropagation();
    remove(index);
  };

  return (
    <div className={`income_source-title tabspoiler_title_${index}`}>
      <div className="cCol cCol__inline bold income_source-title__text">{title}</div>
      <div className="cCol cCol__right">
        <a className="link link_delete" onClick={handleLinkClick}>
          Удалить
        </a>
      </div>
    </div>
  );
}

/**
 * @param {string} title
 * @param {string|number} sum
 * @returns {Node}
 */
export const buildTitleText = (title, sum) => {
  const incomeSumStr = parseFloat(sum) ? (
    <span>
      {' '}
      {', ' + sum}
      <span className="icon-rouble" />
    </span>
  ) : (
    ''
  );
  return (
    <div className={'IncomeSpoilerComponent__title-header'}>
      <span className={'IncomeSpoilerComponent__title-text'}>{title}</span>
      {incomeSumStr}
    </div>
  );
};

/**
 * @param {number} incomeCode
 * @param {number} [taxDeductionCode]
 * @returns {boolean}
 */
export function is14_4FormatDeductionSum(incomeCode, taxDeductionCode) {
  return incomeCode === 1530 && taxDeductionCode === 201;
}
